/**
 * iter 67 — Branding settings panel (admin-only, white-label tier surface).
 *
 * Settings → BRANDING tab. Backed by `GET/PATCH /api/brand` (admin gate
 * already enforced server-side). Lets org admins:
 *   • Paste a logo URL (we don't host file uploads here — the org's CDN
 *     of choice provides the URL; future iteration may add Supabase
 *     Storage upload as a convenience).
 *   • Pick primary + accent brand colours via native <input type='color'>
 *     PLUS a synced hex text field for paste-from-Figma workflows.
 *   • See a live <TopBarPreview> miniature that reflects the unsaved
 *     edits in real time, so admins know exactly what's about to ship.
 *
 * On save we PATCH /api/brand and refresh the BrandContext so the actual
 * shell reflects the new colours within the same render cycle — no full
 * reload needed.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { useBrand } from "./AppShell";
import { Field } from "./UI";
import ImageUpload from "./ImageUpload";
import { ErrorBanner } from "./OrgAdmin/_managerShared";

const HEX_RE = /^#([0-9a-fA-F]{3,8})$/;

function normaliseHex(v) {
  if (!v) return "";
  const s = v.trim();
  if (!s) return "";
  return s.startsWith("#") ? s : `#${s}`;
}

export default function BrandingSection() {
  const { brand, refresh } = useBrand();
  const [form, setForm] = useState({
    logo_url: "",
    brand_primary_color: "",
    brand_accent_color: "",
    custom_domain: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Hydrate from BrandContext whenever it changes.
  useEffect(() => {
    setForm({
      logo_url: brand.logo_url || "",
      brand_primary_color: brand.brand_primary_color || "",
      brand_accent_color: brand.brand_accent_color || "",
      custom_domain: brand.custom_domain || "",
    });
  }, [brand.logo_url, brand.brand_primary_color, brand.brand_accent_color, brand.custom_domain]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // White-label tier ($250/mo) is the intended surface for custom_domain.
  // We soft-gate the UI: lower tiers see the input read-only with an
  // upsell hint, but the backend still stores whatever value we send —
  // that way an admin can pre-configure the domain BEFORE Stripe fires
  // the plan upgrade webhook.
  const isWhiteLabel = brand.plan === "white_label";

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = {};
      payload.logo_url = form.logo_url || "";
      payload.brand_primary_color = form.brand_primary_color || "";
      payload.brand_accent_color = form.brand_accent_color || "";
      payload.custom_domain = form.custom_domain.trim();
      // Hex pre-validation so we don't waste a round-trip.
      for (const k of ["brand_primary_color", "brand_accent_color"]) {
        const v = payload[k];
        if (v && !HEX_RE.test(v)) {
          throw new Error(`${k.replace("brand_", "").replace("_color", "")} must be a CSS hex like #1a73e8.`);
        }
      }
      await api.patch("/brand", payload);
      toast.success("Branding saved.");
      await refresh?.();
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Clear all branding (logo, both colours, and custom domain)?")) return;
    setBusy(true); setErr("");
    try {
      await api.patch("/brand", {
        logo_url: "",
        brand_primary_color: "",
        brand_accent_color: "",
        custom_domain: "",
      });
      toast.success("Branding cleared.");
      await refresh?.();
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="surface p-5 space-y-5" data-testid="branding-section">
      <div>
        <div className="label-xs mb-1">BRANDING</div>
        <p className="text-sm text-muted-foreground">
          Customise the LogBook shell with your organisation&apos;s logo and colours.
          Changes apply to every user in your organisation as soon as you save.
        </p>
      </div>

      <ErrorBanner err={err} testid="branding-error" />

      <Field label="LOGO">
        <div className="flex items-center gap-3">
          <ImageUpload
            endpoint="/uploads/company/logo"
            currentUrl={form.logo_url}
            onUploaded={(url) => {
              setField("logo_url", url);
              toast.success("Logo uploaded. Click SAVE BRANDING to apply.");
            }}
            size={72}
            round={false}
            label="Upload logo"
            testid="branding-logo-upload"
          />
          <div className="flex-1">
            <input
              className="input"
              type="url"
              value={form.logo_url}
              onChange={(e) => setField("logo_url", e.target.value)}
              placeholder="…or paste a public URL"
              data-testid="branding-logo-url-input"
            />
            <div className="text-[11px] text-muted-foreground mt-1">
              Drop a PNG/SVG (transparent background, ~140×40 recommended) — uploaded straight to your Supabase Storage bucket.
              Or paste a public URL from your own CDN. Leave empty to fall back to your organisation name.
            </div>
          </div>
        </div>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <ColorPickerField
          label="PRIMARY COLOUR"
          value={form.brand_primary_color}
          onChange={(v) => setField("brand_primary_color", v)}
          testid="branding-primary"
        />
        <ColorPickerField
          label="ACCENT COLOUR"
          value={form.brand_accent_color}
          onChange={(v) => setField("brand_accent_color", v)}
          testid="branding-accent"
        />
      </div>

      {/* iter 73.9 — Custom domain (white-label tier). Soft-gated with an
          upsell hint on lower tiers but still writable so an admin can
          pre-configure the domain before Stripe fires the plan upgrade. */}
      <Field label="CUSTOM DOMAIN">
        <input
          className="input mono"
          type="text"
          value={form.custom_domain}
          onChange={(e) => setField("custom_domain", e.target.value)}
          placeholder="portal.acme.com"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          data-testid="branding-custom-domain-input"
        />
        <div className="text-[11px] text-muted-foreground mt-1">
          {isWhiteLabel ? (
            <>
              Outbound emails (password resets, invitations, security
              alerts) will link to <span className="mono">https://{form.custom_domain || "portal.acme.com"}</span>{" "}
              instead of the shared LogBook Legal app. Point a CNAME at{" "}
              <span className="mono">app.logbooklegal.com</span> — no server
              config on your side.
            </>
          ) : (
            <>
              <span className="text-amber-600 font-semibold" data-testid="branding-custom-domain-upsell">
                White-label tier ($250/mo).
              </span>{" "}
              You can pre-configure the hostname now; it activates the
              moment your plan upgrades.
            </>
          )}
        </div>
      </Field>

      <div>
        <div className="label-xs mb-1.5">LIVE PREVIEW</div>
        <TopBarPreview
          logoUrl={form.logo_url}
          orgName={brand.org_name || "Your Organisation"}
          primary={form.brand_primary_color}
          accent={form.brand_accent_color}
        />
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-border">
        <button
          type="button"
          className="btn btn-ghost text-destructive"
          onClick={reset}
          disabled={busy}
          data-testid="branding-reset-btn"
        >
          Reset branding
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={save}
          disabled={busy}
          data-testid="branding-save-btn"
        >
          {busy ? "SAVING…" : "SAVE BRANDING"}
        </button>
      </div>
    </div>
  );
}

function ColorPickerField({ label, value, onChange, testid }) {
  const hex = normaliseHex(value);
  // Native colour inputs only accept #rrggbb; gracefully degrade.
  const colorSafe = /^#[0-9a-fA-F]{6}$/.test(hex) ? hex : "#000000";
  return (
    <Field label={label}>
      <div className="flex items-center gap-2">
        <input
          type="color"
          className="h-9 w-10 cursor-pointer border border-border rounded"
          value={colorSafe}
          onChange={(e) => onChange(e.target.value.toLowerCase())}
          data-testid={`${testid}-color-picker`}
        />
        <input
          className="input mono text-xs flex-1"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#1a73e8"
          data-testid={`${testid}-hex-input`}
        />
      </div>
    </Field>
  );
}

function TopBarPreview({ logoUrl, orgName, primary, accent }) {
  const primaryOk = primary && HEX_RE.test(primary) ? primary : null;
  const accentOk = accent && HEX_RE.test(accent) ? accent : null;
  return (
    <div
      className="surface border-2 px-4 py-3 flex items-center justify-between"
      data-testid="branding-preview"
      style={{
        borderColor: primaryOk || undefined,
        background: primaryOk ? `linear-gradient(90deg, ${primaryOk}10, transparent)` : undefined,
      }}
    >
      <div className="flex items-center gap-2 min-w-0">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={orgName}
            className="h-7 max-w-[140px] object-contain"
            data-testid="branding-preview-logo"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <span className="text-sm font-semibold truncate" data-testid="branding-preview-orgname">{orgName}</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span
          className="inline-block h-6 w-6 rounded-full border border-border"
          style={{ backgroundColor: primaryOk || "var(--muted)" }}
          title="Primary"
          data-testid="branding-preview-primary-swatch"
        />
        <span
          className="inline-block h-6 w-6 rounded-full border border-border"
          style={{ backgroundColor: accentOk || "var(--muted)" }}
          title="Accent"
          data-testid="branding-preview-accent-swatch"
        />
      </div>
    </div>
  );
}
